import api from "js/api.js";
import confirmationMixin from "@/v2/mixins/bo-confirmation.js";
import { Prompt } from "@/v2/models/bo-confirmation.js";
import Vue from "vue";
import { pageLoadingOverlay, syncPageBusyFlag, showPageLoading, hidePageLoading } from "@/v2/utils/page-loading.js";

// #111741: the veil is driven by one page-global active-request count (window-shared, like
// window.EventBus) so two copies of this mixin cannot fight over the single body class.
// Reads are veiled only if still in flight after the delay; writes veil at once. Direct
// Web opts in via window.bo.deferLoadingOverlay.
function beginRequest(isWrite) {
    const overlay = pageLoadingOverlay();
    if (++window.__boActiveRequests === 1) {
        overlay.show("counter", { immediate: !window.bo?.deferLoadingOverlay || isWrite });
    }
    if (isWrite) {
        overlay.promote(); // a write behind a pending read must not wait for the read's timer
    }
    syncPageBusyFlag(overlay);
}
// #112748: 404 on the open document's own API url (or a sub-resource like notes) = it was deleted,
// e.g. in another tab. Page-wide via window.bo so every component on a document page gets the popup.
function isDocumentGone(error) {
    const id = window.bo?.documentId ?? window.bo?.proformaInvoiceId;
    const url = (error?.response?.config?.url ?? "").split("?")[0];
    const documentUrl = `${window.bo?.documentsUrl}/${id}`;
    return (
        !!id &&
        !!window.bo?.documentGoneMessage &&
        error?.response?.status === 404 &&
        (url === documentUrl || url.startsWith(`${documentUrl}/`))
    );
}

// same popup as other admin detail pages; closing it goes back to the overview
function showDocumentGone() {
    alert(window.bo.documentGoneMessage, window.bo.documentGoneTitle, "btn-danger", window.bo.documentOverviewUrl);
}

function endRequest() {
    const overlay = pageLoadingOverlay();
    window.__boActiveRequests = Math.max(0, window.__boActiveRequests - 1);
    if (window.__boActiveRequests === 0) {
        overlay.hide("counter");
    }
    syncPageBusyFlag(overlay);
}

api.defaults.headers.common["X-CSRF-TOKEN"] = document.getElementsByName("X-CSRF-TOKEN")[0].getAttribute("content");
api.defaults.headers.common["x-auth-token"] = localStorage.getItem("token_" + window.bo_site);
api.defaults.headers.common["Authorization"] = "Bearer " + localStorage.getItem("token_" + window.bo_site);

export default {
    mixins: [confirmationMixin],
    data() {
        return {
            isLoading: 0,
        };
    },
    mounted() {
        if (!window.EventBus) {
            window.EventBus = new Vue();
        }
        window.EventBus.$on("is-loading", (loadingNumber) => (this.isLoading += loadingNumber));
    },
    watch: {
        isLoading(n, o) {
            // #111741: the veil is no longer toggled from this per-instance counter — an instance
            // mounted after a request began never saw it and could hide the veil under it. The
            // page-global count in processData() owns the veil; isLoading stays for readers.
            if (n < 0) {
                this.isLoading = 0;
            }
        },
    },
    methods: {
        async get(url, payload) {
            return await api.get(url, payload);
        },

        async post(url, payload) {
            return await api.post(url, payload);
        },

        async put(url, payload) {
            return await api.put(url, payload);
        },

        async delete(url, payload) {
            return await api.delete(url, payload);
        },

        async processData(method, url, payload, success = null, failure = null, showLoadingScreen = true) {
            if (showLoadingScreen) {
                beginRequest(method !== this.get);
                this.isLoading++;
                if (!window.EventBus) {
                    window.EventBus = new Vue();
                    window.EventBus.$on("is-loading", (loadingNumber) => (this.isLoading += loadingNumber));
                }
                window.EventBus.$emit("is-loading", 1);
            }

            let data = null;
            try {
                let response = await method(url, payload);
                data = response.data;
                if (success) await success(response);
            } catch (error) {
                if (isDocumentGone(error)) {
                    showDocumentGone();
                } else if (failure) {
                    await failure(error);
                }
            } finally {
                // released in finally so a throwing success/failure callback cannot leave the
                // loading overlay stuck on screen forever
                if (showLoadingScreen) {
                    endRequest();
                    this.isLoading--;
                    window.EventBus.$emit("is-loading", -1);
                }
            }

            // if (showLoadingScreen) {
            //     this.isLoading--;
            //     window.EventBus.$emit("is-loading", -1);
            // }

            return data;
        },

        async getData(url, payload, success, failure, showLoadingScreen = true) {
            return await this.processData(this.get, url, payload, success, failure, showLoadingScreen);
        },

        async postData(url, payload, success, failure, showLoadingScreen = true) {
            return await this.processData(this.post, url, payload, success, failure, showLoadingScreen);
        },

        postDataWithoutAsync(url, payload, success, failure, showLoadingScreen = true) {
            return this.processData(this.post, url, payload, success, failure, showLoadingScreen);
        },

        async putData(url, payload, success, failure, showLoadingScreen = true) {
            return await this.processData(this.put, url, payload, success, failure, showLoadingScreen);
        },

        async deleteData(url, payload, success, failure, showLoadingScreen = true) {
            return await this.processData(this.delete, url, payload, success, failure, showLoadingScreen);
        },

        async updateData(
            url,
            payload,
            success = this.defaultUpdateSuccessCallback,
            failure = this.defaultUpdateFailureCallback
        ) {
            const response = await this.boPromptIfNeeded(
                new Prompt(
                    () => {
                        return this.putData(url, payload, success, failure);
                    },
                    null,
                    "Update",
                    "Are you sure want to update all the changes?<br>This action cannot be undone.",
                    "Update",
                    "success"
                ),
                true
            );

            return response;
        },

        async postBulkData(
            url,
            create,
            update,
            destroy,
            success = this.defaultUpdateSuccessCallback,
            failure = this.defaultUpdateFailureCallback
        ) {
            return this.postData(
                `${url}/bulk`,
                {
                    create: create,
                    update: update,
                    delete: destroy,
                },
                success,
                failure
            );
        },

        defaultUpdateSuccessCallback(response) {
            alert(response.data.message ?? "Update successful!", "Records Updated", "success");
        },

        defaultUpdateFailureCallback(response) {
            if (response.response.data.message) {
                alert(response.response.data.message);
            } else {
                alert("Unable to update data now.");
            }
        },

        showLoading() {
            // direct callers (outside processData) keep today's immediate veil
            showPageLoading();
        },

        hideLoading() {
            hidePageLoading();
        },

        handleError(error, errorTitle, defaultErrorMessage, { hideConfirmBar = false } = {}) {
            if (typeof this.pageErrorMessages !== "undefined" && !Array.isArray(this.pageErrorMessages)) {
                this.pageErrorMessages = [];
            }

            // The alert modal is one shared singleton, so every branch has to forward the caller's
            // options. Hiding the footer only applies to the alerts that ask for it - e.g. the
            // consolidate e-Invoice checklist, whose rows carry their own status icons.
            const notify = (message) => alert(message, errorTitle, "", null, null, { hideConfirmBar });

            if (error.response && error.response.data) {
                if (error.response.data instanceof Blob) {
                    // Download responses use Blob even when the server returns a JSON error.
                    var reader = new FileReader();
                    reader.onload = function (event) {
                        try {
                            var errorData = JSON.parse(event.target.result);
                            var errorDetails = Array.isArray(errorData?.errors)
                                ? errorData.errors[0]
                                : errorData?.errors;
                            var errorMessage = errorData?.message ?? errorDetails?.message;

                            notify(errorMessage ?? defaultErrorMessage);
                        } catch (parseError) {
                            console.error("Failed to parse download error response:", parseError);
                            notify(defaultErrorMessage);
                        }
                    };
                    reader.onerror = function () {
                        notify(defaultErrorMessage);
                    };
                    reader.readAsText(error.response.data);
                } else if (error.response.data.errors) {
                    // validation failed
                    if (error.response.data.errors.code === "repository_validation_error") {
                        const data = error.response.data.errors.data;
                        let scrollToElement = true;
                        Object.entries(data).forEach(([errorId, errorMessages]) => {
                            const errorElementId = `${errorId}_label`;
                            const errorFirstMessage = errorMessages[0];
                            this.errors[errorId] = errorFirstMessage;
                            if (scrollToElement) {
                                this.scrollToErrorElement(errorElementId);
                                scrollToElement = false;
                            }

                            if (typeof this.pageErrorMessages !== "undefined") {
                                if (errorMessages.length > 1 && window.bo?.hideFirstErrorForErrorBar == true) {
                                    errorMessages.shift();
                                }
                                errorMessages.forEach((errorMessage) => {
                                    this.pageErrorMessages.push({
                                        id: errorId,
                                        error_message: errorMessage,
                                    });
                                });
                            }
                        });
                    } else if (error.response.data.errors.length > 0) {
                        notify(error.response.data.errors[0].message);
                    } else {
                        notify(error.response.data.errors.message);
                    }
                } else if (error.response.data.message) {
                    notify(error.response.data.message);
                } else {
                    notify(defaultErrorMessage);
                }
            } else {
                notify(defaultErrorMessage);
            }
        },

        replaceVariables(string, variables) {
            let result = string;
            for (const [placeholder, value] of Object.entries(variables)) {
                result = result.replace(new RegExp(placeholder, "g"), value);
            }
            return result;
        },
    },
};
